/**
 * Waste Stock & Movement Ledger — Mobile-first responsive design
 * Tab 1: Stock — Cards (mobile) / Table (desktop) per category with progress bars
 * Tab 2: Ledger — Timeline cards (mobile) / Table (desktop)
 * Tab 3: Calculator — Full economics breakdown with decision card
 */
import { useState } from 'react'
import {
  App, Alert, Button, Card, Col, DatePicker, Empty, Form, Grid, Input, InputNumber,
  Modal, Progress, Row, Select, Space, Table, Tabs, Tag, Tooltip, Typography,
} from 'antd'
import {
  ReloadOutlined, CalculatorOutlined, ToolOutlined, PlusOutlined,
  ArrowDownOutlined, ArrowUpOutlined, RetweetOutlined,
  ShoppingOutlined, SwapOutlined, DeleteOutlined, InboxOutlined,
  RiseOutlined, FallOutlined, CheckCircleFilled, WarningFilled, CloseCircleFilled,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { wasteApi } from '@/features/waste/api'
import type { WasteStock, WasteMovement, ReuseEconomics } from '@/features/waste/api'
import { usePlantId } from '@/shared/hooks/usePlantId'
import PageHeader from '@/shared/components/PageHeader'
import KPICard from '@/shared/components/KPICard'
import { usePermission } from '@/shared/hooks/usePermission'
import dayjs from 'dayjs'

const { Text, Title } = Typography
const { useBreakpoint } = Grid

// ── Movement icon + color per type ───────────────────────────────────────────
const MOVEMENT_META: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  IN_PRODUCTION:       { color: '#52c41a', icon: <ArrowUpOutlined />, label: 'In — Production' },
  IN_RECEIPT_TRANSFER: { color: '#13c2c2', icon: <ArrowUpOutlined />, label: 'In — Transfer Receipt' },
  OUT_REUSE:           { color: '#1677ff', icon: <RetweetOutlined />, label: 'Out — Reuse' },
  OUT_SELL:            { color: '#faad14', icon: <ShoppingOutlined />, label: 'Out — Sale' },
  OUT_TRANSFER:        { color: '#722ed1', icon: <SwapOutlined />, label: 'Out — Transfer' },
  OUT_DISPOSE:         { color: '#ff4d4f', icon: <DeleteOutlined />, label: 'Out — Dispose' },
  ADJ_PHYSICAL:        { color: '#fa8c16', icon: <ToolOutlined />, label: 'Adjustment' },
}

// ── Decision card visuals ───────────────────────────────────────────────────
const DECISION_META = {
  REUSE_GOOD:        { color: '#52c41a', bg: '#f6ffed', icon: <CheckCircleFilled />, label: 'Strong Saving — Reuse Recommended' },
  REUSE_MARGINAL:    { color: '#faad14', bg: '#fffbe6', icon: <WarningFilled />,     label: 'Marginal — Decide Case by Case' },
  SELL_RECOMMENDED:  { color: '#ff4d4f', bg: '#fff2f0', icon: <CloseCircleFilled />, label: 'Net LOSS — Sell Instead' },
}

// ── Disposition tag ─────────────────────────────────────────────────────────
function DispositionTag({ type }: { type?: string }) {
  if (!type) return null
  const map: Record<string, { color: string; icon: React.ReactNode; text: string }> = {
    REUSE:    { color: 'green',  icon: <RetweetOutlined />,  text: 'Reuse' },
    SELL:     { color: 'gold',   icon: <ShoppingOutlined />, text: 'Sell' },
    TRANSFER: { color: 'blue',   icon: <SwapOutlined />,     text: 'Transfer' },
    DISPOSE:  { color: 'red',    icon: <DeleteOutlined />,   text: 'Dispose' },
  }
  const m = map[type] ?? { color: 'default', icon: null, text: type }
  return <Tag color={m.color} icon={m.icon}>{m.text}</Tag>
}

export default function WasteStockPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const screens = useBreakpoint()
  const isMobile = !screens.md

  const canAdjust = usePermission('waste.admin')
  const canManualIn = usePermission('waste.classify')

  const { data: stock = [], isLoading } = useQuery({
    queryKey: ['waste-stock', plantId],
    queryFn: () => wasteApi.listStock(plantId as string),
    enabled: !!plantId,
  })

  const { data: categories = [] } = useQuery({
    queryKey: ['waste-categories', plantId],
    queryFn: () => wasteApi.listCategories(plantId as string),
    enabled: !!plantId,
  })

  const [movFilters, setMovFilters] = useState<Record<string, unknown>>({})
  const { data: movements = [], isLoading: movLoading } = useQuery({
    queryKey: ['waste-movements', plantId, movFilters],
    queryFn: () => wasteApi.listMovements(plantId as string, movFilters),
    enabled: !!plantId,
  })

  // Manual IN modal
  const [inOpen, setInOpen] = useState(false)
  const [inForm] = Form.useForm()
  const inMutation = useMutation({
    mutationFn: (v: any) => wasteApi.manualIn({ ...v, plant_id: plantId, movement_type: 'IN_PRODUCTION' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['waste-stock'] })
      qc.invalidateQueries({ queryKey: ['waste-movements'] })
      message.success('Waste added to stock')
      setInOpen(false); inForm.resetFields()
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Failed'),
  })

  // Adjustment modal
  const [adjOpen, setAdjOpen] = useState(false)
  const [adjForm] = Form.useForm()
  const adjMutation = useMutation({
    mutationFn: (v: any) => wasteApi.adjust({ ...v, plant_id: plantId }),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['waste-stock'] })
      qc.invalidateQueries({ queryKey: ['waste-movements'] })
      // GOV-001 P5 — adjustments now apply only after Plant Head
      // (and CFO for large values) approve.
      if (r?.approval_status === 'PENDING_APPROVAL') {
        message.info(r.message ?? 'Adjustment sent for approval')
      } else {
        message.success('Stock adjusted')
      }
      setAdjOpen(false); adjForm.resetFields()
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Failed'),
  })

  // Calculator
  const [econOpen, setEconOpen] = useState(false)
  const [econForm] = Form.useForm()
  const [econResult, setEconResult] = useState<ReuseEconomics | null>(null)
  const econMutation = useMutation({
    mutationFn: (v: any) => wasteApi.reuseEconomics({ ...v, plant_id: plantId }),
    onSuccess: (r) => setEconResult(r),
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Failed'),
  })

  // Stock totals + grouping
  const totals = stock.reduce(
    (acc: any, s: any) => ({
      totalKg: acc.totalKg + Number(s.current_balance_kg || 0),
      totalValue: acc.totalValue + Number(s.total_value || 0),
      reusableKg: acc.reusableKg + (categories.find(c => c.id === s.category_id)?.is_reusable ? Number(s.current_balance_kg || 0) : 0),
      sellKg: acc.sellKg + (categories.find(c => c.id === s.category_id)?.default_disposition === 'SELL' ? Number(s.current_balance_kg || 0) : 0),
    }),
    { totalKg: 0, totalValue: 0, reusableKg: 0, sellKg: 0 },
  )

  const findCat = (cid: string) => categories.find(c => c.id === cid)

  // ── Stock Card (mobile) ────────────────────────────────────────────────────
  function StockCard({ row }: { row: WasteStock }) {
    const cat = findCat(row.category_id)
    const balance = Number(row.current_balance_kg || 0)
    const value = Number(row.total_value || 0)
    return (
      <Card
        size="small"
        style={{
          marginBottom: 12,
          borderLeft: `4px solid ${cat?.is_reusable ? '#52c41a' : '#faad14'}`,
        }}
        styles={{ body: { padding: 12 } }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text strong style={{ fontSize: 15, display: 'block', wordBreak: 'break-word' }}>
              {row.category_name}
            </Text>
            <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>
              {row.category_code}
            </Text>
          </div>
          <DispositionTag type={cat?.default_disposition} />
        </div>
        <Row gutter={8}>
          <Col span={12}>
            <Text type="secondary" style={{ fontSize: 11 }}>Balance</Text>
            <div style={{ fontSize: 18, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              {balance.toFixed(1)} <span style={{ fontSize: 12, color: '#8c8c8c' }}>kg</span>
            </div>
          </Col>
          <Col span={12}>
            <Text type="secondary" style={{ fontSize: 11 }}>Value</Text>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#1677ff', fontVariantNumeric: 'tabular-nums' }}>
              {value > 0 ? `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—'}
            </div>
          </Col>
        </Row>
        {row.last_in_date && (
          <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
            Last in: {dayjs(row.last_in_date).format('DD-MMM HH:mm')}
          </Text>
        )}
        {canAdjust && (
          <Button
            block
            size="middle"
            icon={<ToolOutlined />}
            style={{ marginTop: 8, height: 38 }}
            onClick={() => {
              adjForm.setFieldsValue({ category_id: row.category_id, new_balance_kg: balance })
              setAdjOpen(true)
            }}
          >
            Adjust
          </Button>
        )}
      </Card>
    )
  }

  // ── Movement Card (mobile) ─────────────────────────────────────────────────
  function MovementCard({ row }: { row: WasteMovement }) {
    const meta = MOVEMENT_META[row.movement_type] ?? { color: '#8c8c8c', icon: null, label: row.movement_type }
    const isIn = row.movement_type.startsWith('IN_') || (row.movement_type === 'ADJ_PHYSICAL' && Number(row.qty_kg) > 0)
    return (
      <Card size="small" style={{ marginBottom: 8, borderLeft: `3px solid ${meta.color}` }} styles={{ body: { padding: 10 } }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: `${meta.color}22`, color: meta.color,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
            flexShrink: 0,
          }}>{meta.icon}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 4 }}>
              <Text strong style={{ fontSize: 13 }}>{row.category_name}</Text>
              <Text style={{ fontSize: 16, fontWeight: 700, color: isIn ? '#52c41a' : '#ff4d4f', fontVariantNumeric: 'tabular-nums' }}>
                {isIn ? '+' : '−'}{Math.abs(Number(row.qty_kg)).toFixed(1)} kg
              </Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2, fontSize: 11, color: '#8c8c8c' }}>
              <span>{meta.label}</span>
              <span>{dayjs(row.created_at).format('DD-MMM HH:mm')}</span>
            </div>
            {row.total_value && (
              <Text style={{ fontSize: 12, color: '#595959' }}>
                ₹{Number(row.total_value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </Text>
            )}
            {row.remarks && <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{row.remarks}</Text>}
          </div>
        </div>
      </Card>
    )
  }

  // ── Stock Table (desktop) ──────────────────────────────────────────────────
  const stockColumns = [
    { title: 'Category', key: 'cat',
      render: (_: unknown, r: WasteStock) => {
        const cat = findCat(r.category_id)
        return (
          <Space direction="vertical" size={2}>
            <Text strong>{r.category_name}</Text>
            <Space size={4}>
              <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>{r.category_code}</Text>
              <DispositionTag type={cat?.default_disposition} />
            </Space>
          </Space>
        )
      }
    },
    { title: 'Balance', dataIndex: 'current_balance_kg', align: 'right' as const, width: 130,
      render: (v: any) => <Text strong style={{ fontSize: 15, fontVariantNumeric: 'tabular-nums' }}>{Number(v || 0).toFixed(2)} kg</Text> },
    { title: 'Locked', dataIndex: 'locked_kg', align: 'right' as const, width: 100,
      render: (v: any) => <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{Number(v || 0).toFixed(2)} kg</Text> },
    { title: 'Avg ₹/kg', dataIndex: 'avg_landed_cost_per_kg', align: 'right' as const, width: 110,
      render: (v: any) => v ? <Text style={{ fontVariantNumeric: 'tabular-nums' }}>₹{Number(v).toFixed(2)}</Text> : '—' },
    { title: 'Stock Value', dataIndex: 'total_value', align: 'right' as const, width: 140,
      render: (v: any) => v
        ? <Text strong style={{ color: '#1677ff', fontVariantNumeric: 'tabular-nums' }}>₹{Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</Text>
        : '—' },
    { title: 'Last In', dataIndex: 'last_in_date', width: 140,
      render: (v?: string) => v ? <Text style={{ fontSize: 12 }}>{dayjs(v).format('DD-MMM HH:mm')}</Text> : '—' },
    { title: '', key: 'action', width: 90,
      render: (_: unknown, r: WasteStock) => canAdjust && (
        <Button size="small" icon={<ToolOutlined />} onClick={() => {
          adjForm.setFieldsValue({ category_id: r.category_id, new_balance_kg: Number(r.current_balance_kg) })
          setAdjOpen(true)
        }}>Adjust</Button>
      ),
    },
  ]

  // ── Movement Table (desktop) ───────────────────────────────────────────────
  const movColumns = [
    { title: 'When', dataIndex: 'created_at', width: 140,
      render: (v: string) => <Text style={{ fontSize: 12 }}>{dayjs(v).format('DD-MMM HH:mm')}</Text> },
    { title: 'Type', dataIndex: 'movement_type', width: 180,
      render: (v: string) => {
        const m = MOVEMENT_META[v]
        return m ? <Tag icon={m.icon} color={
          v.startsWith('IN_') ? 'green' : v === 'ADJ_PHYSICAL' ? 'orange' : v === 'OUT_REUSE' ? 'blue' : v === 'OUT_SELL' ? 'gold' : v === 'OUT_TRANSFER' ? 'purple' : 'red'
        }>{m.label}</Tag> : <Tag>{v}</Tag>
      } },
    { title: 'Category', key: 'cat',
      render: (_: unknown, r: WasteMovement) => (
        <div>
          <Text style={{ fontSize: 12 }}>{r.category_name}</Text>
          <br /><Text type="secondary" style={{ fontSize: 10, fontFamily: 'monospace' }}>{r.category_code}</Text>
        </div>
      ) },
    { title: 'Qty', dataIndex: 'qty_kg', align: 'right' as const, width: 110,
      render: (v: any, r: WasteMovement) => {
        const isIn = r.movement_type.startsWith('IN_') || (r.movement_type === 'ADJ_PHYSICAL' && Number(v) > 0)
        return (
          <Text strong style={{ color: isIn ? '#52c41a' : '#ff4d4f', fontVariantNumeric: 'tabular-nums' }}>
            {isIn ? '+' : '−'}{Math.abs(Number(v)).toFixed(2)} kg
          </Text>
        )
      },
    },
    { title: 'Balance', dataIndex: 'balance_after_kg', align: 'right' as const, width: 110,
      render: (v: any) => <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{Number(v).toFixed(2)} kg</Text> },
    { title: '₹/kg', dataIndex: 'unit_value_per_kg', align: 'right' as const, width: 90,
      render: (v: any, r: WasteMovement) => {
        const val = v || r.unit_cost_per_kg
        return val ? <Text style={{ fontVariantNumeric: 'tabular-nums' }}>₹{Number(val).toFixed(2)}</Text> : '—'
      },
    },
    { title: 'Total ₹', dataIndex: 'total_value', align: 'right' as const, width: 120,
      render: (v: any) => v ? <Text style={{ fontVariantNumeric: 'tabular-nums' }}>₹{Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</Text> : '—' },
    { title: 'Notes', key: 'notes',
      render: (_: unknown, r: WasteMovement) => (
        <Space direction="vertical" size={0} style={{ fontSize: 11 }}>
          {r.source_dept && <Tag color="blue">{r.source_dept}</Tag>}
          {r.remarks && <Text type="secondary">{r.remarks}</Text>}
        </Space>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Waste Stock & Ledger"
        subtitle="Track every kg of waste from generation to disposition. Mobile-friendly, real-time."
      />

      {/* ── Top KPI Strip ────────────────────────────────────────────────── */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={12} md={6}>
          <KPICard label="Total Stock" value={totals.totalKg} suffix=" kg" precision={1}
            icon={<InboxOutlined />} color="blue" loading={isLoading} />
        </Col>
        <Col xs={12} sm={12} md={6}>
          <KPICard label="Stock Value" value={totals.totalValue} prefix="₹"
            icon={<RiseOutlined />} color="purple" loading={isLoading} />
        </Col>
        <Col xs={12} sm={12} md={6}>
          <KPICard label="Reusable" value={totals.reusableKg} suffix=" kg" precision={1}
            icon={<RetweetOutlined />} color="green" loading={isLoading}
            hint={`${stock.length} categories`} />
        </Col>
        <Col xs={12} sm={12} md={6}>
          <KPICard label="For Sale" value={totals.sellKg} suffix=" kg" precision={1}
            icon={<ShoppingOutlined />} color="gold" loading={isLoading} />
        </Col>
      </Row>

      {/* ── Action Bar ───────────────────────────────────────────────────── */}
      <Card size="small" style={{ marginBottom: 12 }} styles={{ body: { padding: 10 } }}>
        <Space wrap>
          <Button
            type="primary"
            icon={<CalculatorOutlined />}
            size={isMobile ? 'large' : 'middle'}
            onClick={() => { econForm.resetFields(); setEconResult(null); setEconOpen(true) }}
          >
            Reuse Saving Calculator
          </Button>
          {canManualIn && (
            <Button
              icon={<PlusOutlined />}
              size={isMobile ? 'large' : 'middle'}
              onClick={() => setInOpen(true)}
            >
              Manual IN Entry
            </Button>
          )}
          <Button
            icon={<ReloadOutlined />}
            size={isMobile ? 'large' : 'middle'}
            onClick={() => {
              qc.invalidateQueries({ queryKey: ['waste-stock'] })
              qc.invalidateQueries({ queryKey: ['waste-movements'] })
            }}
          >
            Refresh
          </Button>
        </Space>
      </Card>

      <Tabs
        size={isMobile ? 'large' : 'middle'}
        items={[
          {
            key: 'stock', label: `Stock (${stock.length})`,
            children: isMobile ? (
              <div>
                {isLoading ? <Card loading /> :
                  stock.length === 0 ? <Empty description="No stock yet" /> :
                  stock.map((s: any) => <StockCard key={s.id} row={s} />)
                }
              </div>
            ) : (
              <Card styles={{ body: { padding: 0 } }}>
                <Table rowKey="id" columns={stockColumns} dataSource={stock} loading={isLoading}
                  size="middle" pagination={false} />
              </Card>
            ),
          },
          {
            key: 'mov', label: `Ledger (${movements.length})`,
            children: (
              <div>
                <Card size="small" style={{ marginBottom: 12 }} styles={{ body: { padding: 10 } }}>
                  <Space wrap style={{ width: '100%' }}>
                    <Select
                      placeholder="Filter category" allowClear style={{ minWidth: 200 }}
                      options={categories.map(c => ({ value: c.id, label: c.name }))}
                      onChange={v => setMovFilters({ ...movFilters, category_id: v })}
                    />
                    <Select
                      placeholder="Filter type" allowClear style={{ minWidth: 180 }}
                      options={Object.entries(MOVEMENT_META).map(([v, m]) => ({ value: v, label: m.label }))}
                      onChange={v => setMovFilters({ ...movFilters, movement_type: v })}
                    />
                    <DatePicker.RangePicker
                      onChange={(d) => setMovFilters({
                        ...movFilters,
                        from_date: d?.[0]?.format('YYYY-MM-DD'),
                        to_date: d?.[1]?.format('YYYY-MM-DD'),
                      })}
                    />
                  </Space>
                </Card>

                {isMobile ? (
                  <div>
                    {movLoading ? <Card loading /> :
                      movements.length === 0 ? <Empty description="No movements yet" /> :
                      movements.map((m: any) => <MovementCard key={m.id} row={m} />)
                    }
                  </div>
                ) : (
                  <Card styles={{ body: { padding: 0 } }}>
                    <Table rowKey="id" columns={movColumns} dataSource={movements} loading={movLoading}
                      size="small" pagination={{ pageSize: 50 }} scroll={{ x: 1100 }} />
                  </Card>
                )}
              </div>
            ),
          },
        ]}
      />

      {/* ── Reuse Economics Calculator Modal ─────────────────────────────── */}
      <Modal
        title={<Space><CalculatorOutlined />Reuse Saving Calculator</Space>}
        open={econOpen}
        onCancel={() => { setEconOpen(false); setEconResult(null) }}
        onOk={() => econForm.validateFields().then(v => econMutation.mutate(v))}
        confirmLoading={econMutation.isPending}
        okText="Calculate"
        width={isMobile ? '95%' : 720}
      >
        <Alert
          message="True Saving Formula"
          description="Avoided cotton − Reprocessing cost − Secondary waste loss − Opportunity cost"
          type="info" showIcon style={{ marginBottom: 16 }}
        />
        <Form form={econForm} layout="vertical">
          <Form.Item name="category_id" label="Reusable Category" rules={[{ required: true }]}>
            <Select size="large" showSearch optionFilterProp="label"
              placeholder="Select category"
              options={categories.filter(c => c.is_reusable).map(c => ({
                value: c.id,
                label: `${c.code} — ${c.name} (yield ${c.effective_yield_pct ?? '—'}%)`,
              }))} />
          </Form.Item>
          <Form.Item name="qty_kg" label="Qty to Reuse (kg)" rules={[{ required: true }]}>
            <InputNumber size="large" style={{ width: '100%' }} min={0.1} step={1} />
          </Form.Item>
          <Form.Item name="fresh_cotton_landed_per_kg" label="Fresh Cotton ₹/kg (optional)"
            help="Leave empty to use plant median">
            <InputNumber size="large" style={{ width: '100%' }} min={0} step={1} />
          </Form.Item>
        </Form>

        {econResult && (
          <div style={{ marginTop: 20 }}>
            {/* Decision Banner */}
            <div style={{
              background: DECISION_META[econResult.decision].bg,
              border: `2px solid ${DECISION_META[econResult.decision].color}`,
              borderRadius: 12, padding: 16, marginBottom: 16,
            }}>
              <Space size={12} style={{ marginBottom: 8 }}>
                <span style={{ fontSize: 24, color: DECISION_META[econResult.decision].color }}>
                  {DECISION_META[econResult.decision].icon}
                </span>
                <Text strong style={{ fontSize: 15, color: DECISION_META[econResult.decision].color }}>
                  {DECISION_META[econResult.decision].label}
                </Text>
              </Space>
              <Row gutter={16}>
                <Col xs={12}>
                  <Text type="secondary" style={{ fontSize: 11 }}>NET BENEFIT</Text>
                  <div style={{
                    fontSize: 26, fontWeight: 800,
                    color: Number(econResult.net_economic_benefit) >= 0 ? '#52c41a' : '#ff4d4f',
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    ₹{Number(econResult.net_economic_benefit).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </div>
                </Col>
                <Col xs={12}>
                  <Text type="secondary" style={{ fontSize: 11 }}>PER KG</Text>
                  <div style={{
                    fontSize: 26, fontWeight: 800,
                    color: Number(econResult.benefit_per_kg) >= 0 ? '#52c41a' : '#ff4d4f',
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    ₹{Number(econResult.benefit_per_kg).toFixed(0)}
                  </div>
                </Col>
              </Row>
            </div>

            {/* Breakdown */}
            <Card size="small" title="Cost-Benefit Breakdown" styles={{ body: { padding: 0 } }}>
              {[
                { l: '+ Avoided Cotton Purchase',  v: econResult.avoided_cotton_cost,  pos: true },
                { l: '− Reprocessing Cost',        v: -Number(econResult.reprocessing_cost),       pos: false, hint: 'Power + labor + overhead' },
                { l: `− Secondary Waste Loss`,     v: -Number(econResult.secondary_loss_value),    pos: false, hint: `${Number(econResult.secondary_waste_kg).toFixed(0)} kg lost during reprocessing` },
                { l: '− Opportunity Cost (Sale)',  v: -Number(econResult.opportunity_cost_sale),   pos: false, hint: 'What you would have earned by selling' },
              ].map((row, i) => (
                <div key={i} style={{
                  padding: '10px 14px',
                  borderBottom: i < 3 ? '1px solid #f0f0f0' : 'none',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text style={{ fontSize: 13 }}>{row.l}</Text>
                    {row.hint && <div><Text type="secondary" style={{ fontSize: 10 }}>{row.hint}</Text></div>}
                  </div>
                  <Text strong style={{
                    color: row.pos ? '#52c41a' : '#ff4d4f',
                    fontVariantNumeric: 'tabular-nums',
                    fontSize: 14,
                  }}>
                    ₹{Number(row.v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </Text>
                </div>
              ))}
              <div style={{ padding: '12px 14px', background: '#fafafa', display: 'flex', justifyContent: 'space-between' }}>
                <Text strong>= NET BENEFIT</Text>
                <Text strong style={{
                  color: Number(econResult.net_economic_benefit) >= 0 ? '#52c41a' : '#ff4d4f',
                  fontVariantNumeric: 'tabular-nums', fontSize: 16,
                }}>
                  ₹{Number(econResult.net_economic_benefit).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </Text>
              </div>
            </Card>

            {/* Yield strip */}
            <Card size="small" style={{ marginTop: 12 }} styles={{ body: { padding: '10px 14px' } }}>
              <Space size={20} wrap>
                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>YARN OUTPUT</Text>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#52c41a', fontVariantNumeric: 'tabular-nums' }}>
                    {Number(econResult.yarn_produced_kg).toFixed(0)} kg
                  </div>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>SECONDARY WASTE</Text>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#ff4d4f', fontVariantNumeric: 'tabular-nums' }}>
                    {Number(econResult.secondary_waste_kg).toFixed(0)} kg
                  </div>
                </div>
              </Space>
            </Card>

            {econResult.reasoning.length > 0 && (
              <div style={{ marginTop: 12 }}>
                {econResult.reasoning.map((r, i) =>
                  <Tag key={i} style={{ marginBottom: 4, fontSize: 11 }}>{r}</Tag>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* ── Manual IN Modal ─────────────────────────────────────────────── */}
      <Modal
        title="Manual IN Entry"
        open={inOpen}
        onCancel={() => setInOpen(false)}
        onOk={() => inForm.validateFields().then(v => inMutation.mutate(v))}
        confirmLoading={inMutation.isPending}
        width={isMobile ? '95%' : 600}
      >
        <Form form={inForm} layout="vertical">
          <Form.Item name="category_id" label="Category" rules={[{ required: true }]}>
            <Select size="large" showSearch optionFilterProp="label"
              options={categories.map(c => ({ value: c.id, label: `${c.code} — ${c.name}` }))} />
          </Form.Item>
          <Form.Item name="qty_kg" label="Qty (kg)" rules={[{ required: true }]}>
            <InputNumber size="large" style={{ width: '100%' }} min={0.001} step={0.5} />
          </Form.Item>
          <Form.Item name="unit_cost_per_kg" label="Unit Cost ₹/kg">
            <InputNumber size="large" style={{ width: '100%' }} min={0} step={0.5} />
          </Form.Item>
          <Form.Item name="source_dept" label="Source Department">
            <Select size="large" options={['BLOWROOM', 'CARDING', 'DRAWING', 'COMBING', 'SIMPLEX', 'RING', 'WINDING'].map(v => ({ value: v, label: v }))} />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>

      {/* ── Adjustment Modal ────────────────────────────────────────────── */}
      <Modal
        title="Stock Adjustment (Admin)"
        open={adjOpen}
        onCancel={() => setAdjOpen(false)}
        onOk={() => adjForm.validateFields().then(v => adjMutation.mutate(v))}
        confirmLoading={adjMutation.isPending}
        okText="Adjust"
        width={isMobile ? '95%' : 600}
      >
        <Alert message="Set stock to a new balance. Delta is logged as ADJ_PHYSICAL."
          type="warning" showIcon style={{ marginBottom: 12 }} />
        <Form form={adjForm} layout="vertical">
          <Form.Item name="category_id" label="Category" rules={[{ required: true }]}>
            <Select size="large" showSearch optionFilterProp="label"
              options={categories.map(c => ({ value: c.id, label: `${c.code} — ${c.name}` }))} />
          </Form.Item>
          <Form.Item name="new_balance_kg" label="New Balance (kg)" rules={[{ required: true }]}>
            <InputNumber size="large" style={{ width: '100%' }} min={0} step={0.5} />
          </Form.Item>
          <Form.Item name="reason" label="Reason" rules={[{ required: true, min: 5 }]}>
            <Input.TextArea rows={3} placeholder="Why is this adjustment needed?" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
